import {
  getConfigTenant,
  isCaponeTenant,
  isCorpTenant,
  useUserContext,
} from "@capone/common";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import {
  ActionButton,
  FunnelNewTag,
  FunnelNewTagType,
  HeaderHomePageMiddleSection,
  HeaderMenuButtonType,
  HelpTip,
  Icon,
  IconName,
  VacationRentalNewTag,
} from "halifax";
import { IHeaderHomePageMiddleSectionButtonProps } from "halifax/build/Header/components/HeaderHomePageMiddleSection/component";
import React, { useRef, useState, useEffect } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  CorpSessionInfo,
  ModalNames,
  RewardsAccount,
  defaultCorpColors,
} from "redmond";
import {
  CORP_MARKETPLACE_PACKAGES_NAME,
  CORP_MARKETPLACE_PACKAGES_NAME_VARIANTS,
  TRIP_BUILDER,
  HOTELS_FLIGHTS,
} from "@capone/experiments";
import { config } from "../../../api/config";
import { updateUserSeenModal } from "../../../api/v1/user/updateUserSeenModal";
import {
  AVAILABLE,
  CARS_ONLY,
  CONTROL,
  EXPERIENCES_EXPERIMENT,
  FLIGHTS_ONLY,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  HOTELS_ONLY,
  LC_GATING_EXPERIMENT,
  LUXURY_VACATION_RENTAL_FUNNEL_LAUNCH,
  NOT_SELF_FUNDED,
  PACKAGES_EXPERIMENT,
  PACKAGES_EXPERIMENT_VARIANTS,
  PREMIER_COLLECTION_EXPERIMENT,
  PREMIUM_STAYS_ONLY,
  RESTRICT_FUNNELS,
  RESTRICT_FUNNELS_VARIANTS,
  RestrictFunnelsVariantType,
  SELF_FUNDED,
  STAYS_HOMEPAGE,
  STAYS_VACATION_RENTALS_AWARENESS_MODAL,
  VR_FOR_NON_PREMIUM_CARDHOLDERS_EXPERIMENT,
  useExperiments,
} from "../../../context/experiments";
import {
  PATH_CARS,
  PATH_EXPERIENCES,
  PATH_FLIGHTS,
  PATH_HOTELS,
  PATH_PACKAGES,
  PATH_PREMIER_COLLECTION,
  PATH_STAYS,
} from "../../../utils/urlPaths";

interface FunnelEntryTabsProps extends RouteComponentProps {
  rewardsAccounts?: RewardsAccount[];
  isMobile?: boolean;
}

export const FunnelEntryTabs = (props: FunnelEntryTabsProps) => {
  const { history, isMobile } = props;

  if (isMobile) {
    return null;
  }

  const expState = useExperiments();

  const isCorporate = isCorpTenant(getConfigTenant());

  const { sessionInfo } = useUserContext(getConfigTenant());
  const hasSeenPackagesHelpTipBackend =
    isCorporate && sessionInfo
      ? (sessionInfo as CorpSessionInfo).corporateInfo?.hasSeenModalMap?.[
          ModalNames.PACKAGES_EDUCATIONAL_HELPTIP
        ] ?? false
      : false;

  const vacationFunnelLaunchEnabled =
    getExperimentVariant(
      expState.experiments,
      LUXURY_VACATION_RENTAL_FUNNEL_LAUNCH
    ) === AVAILABLE;

  const isPremierCollectionEnabled =
    getExperimentVariant(
      expState.experiments,
      PREMIER_COLLECTION_EXPERIMENT
    ) === AVAILABLE;

  const isLifestyleEnabled =
    getExperimentVariant(expState.experiments, LC_GATING_EXPERIMENT) ===
    AVAILABLE;

  const isLifestyleOnlyEnabled =
    isLifestyleEnabled && !isPremierCollectionEnabled;

  const isVRForNonPremiumCardHoldersEnabled =
    getExperimentVariant(
      expState.experiments,
      VR_FOR_NON_PREMIUM_CARDHOLDERS_EXPERIMENT
    ) === AVAILABLE;

  const restrictFunnelVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    RESTRICT_FUNNELS,
    RESTRICT_FUNNELS_VARIANTS
  );

  const packagesExperimentVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    PACKAGES_EXPERIMENT,
    PACKAGES_EXPERIMENT_VARIANTS
  );

  const staysHomepageEnabled =
    getExperimentVariant(expState.experiments, STAYS_HOMEPAGE) === AVAILABLE;

  const [showPackagesHelpTip, setShowPackagesHelpTip] = useState(false);
  const packagesButtonRef = useRef<HTMLElement | null>(null);

  const staysVacationRentalsAwarenessModalEnabled =
    getExperimentVariant(
      expState.experiments,
      STAYS_VACATION_RENTALS_AWARENESS_MODAL
    ) === AVAILABLE;
  const staysVRAwarenessModalLocalStorageKey = "vr_awareness_modal_seen";
  const staysVRAwarenessModalSeen =
    localStorage.getItem(staysVRAwarenessModalLocalStorageKey) != null;

  const isPackagesExperiment = [SELF_FUNDED, NOT_SELF_FUNDED].includes(
    packagesExperimentVariant
  );

  const isExperiencesExperiment =
    getExperimentVariant(expState.experiments, EXPERIENCES_EXPERIMENT) ===
    AVAILABLE;

  const corpMarketplacePackagesNameVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    CORP_MARKETPLACE_PACKAGES_NAME,
    CORP_MARKETPLACE_PACKAGES_NAME_VARIANTS
  );

  useEffect(() => {
    if (hasSeenPackagesHelpTipBackend) {
      return;
    }

    setTimeout(() => {
      const packagesTab = document.querySelector(
        ".home-page-header-middle-section-button-wrapper.packages"
      );
      if (packagesTab && isPackagesExperiment) {
        packagesButtonRef.current = packagesTab as HTMLElement;
        setShowPackagesHelpTip(true);
      }
    }, 500);
  }, [isPackagesExperiment, hasSeenPackagesHelpTipBackend]);

  const handleClosePackagesHelpTip = () => {
    setShowPackagesHelpTip(false);
    updateUserSeenModal({
      modalName: ModalNames.PACKAGES_EDUCATIONAL_HELPTIP,
    }).catch((e: Error) => {
      console.error("Issue persisting seen packages helptip", e);
    });
  };

  const getPackagesDisplayName = () => {
    if (!isCorporate) {
      return "Packages";
    }

    switch (corpMarketplacePackagesNameVariant) {
      case TRIP_BUILDER:
        return "Trip Builder";
      case HOTELS_FLIGHTS:
        return "Hotels + Flights";
      case CONTROL:
      default:
        return "Hotels + Flights";
    }
  };

  const hotelsButton = {
    type: staysHomepageEnabled
      ? HeaderMenuButtonType.Stays
      : HeaderMenuButtonType.Hotels,
    selected:
      history.location.pathname === PATH_HOTELS ||
      history.location.pathname === PATH_STAYS,
    content: (
      <Box className="funnel-button-content">
        <Icon name={IconName.HotelFunnelIcon} />
        <Typography>{staysHomepageEnabled ? "Stays" : "Hotels"}</Typography>
        {staysVacationRentalsAwarenessModalEnabled &&
          !staysVRAwarenessModalSeen && (
            <FunnelNewTag
              enabled
              type={FunnelNewTagType.STAYS_FUNNEL_SEARCHES}
            />
          )}
      </Box>
    ),
    onClick: () =>
      history.push(staysHomepageEnabled ? PATH_STAYS : PATH_HOTELS),
  };

  const flightsButton = {
    type: HeaderMenuButtonType.Flights,
    selected: history.location.pathname === PATH_FLIGHTS,
    content: (
      <Box className="funnel-button-content">
        <Icon name={IconName.FlightFunnelIcon} />

        <Typography>Flights</Typography>
      </Box>
    ),
    onClick: () => history.push(PATH_FLIGHTS),
  };

  const carsButton = {
    type: HeaderMenuButtonType.Cars,
    selected: history.location.pathname === PATH_CARS,
    content: (
      <Box className="funnel-button-content">
        <Icon name={IconName.CarFunnelIcon} />
        <Typography>Rental Cars</Typography>
      </Box>
    ),
    onClick: () => history.push(PATH_CARS),
  };

  const showPremiumFunnel = isLifestyleEnabled && isCaponeTenant(config.TENANT);

  const getPremiumStaysButton =
    (): IHeaderHomePageMiddleSectionButtonProps[] => {
      if (isPremierCollectionEnabled || isVRForNonPremiumCardHoldersEnabled) {
        return [
          {
            type: HeaderMenuButtonType.PremierCollection,
            selected: history.location.pathname.includes(
              PATH_PREMIER_COLLECTION
            ),
            content: (
              <Box className="funnel-button-content premium-stays">
                <Icon name={IconName.StarIconWithLines} />
                <Typography>Premium Stays</Typography>
                <VacationRentalNewTag enabled={vacationFunnelLaunchEnabled} />
              </Box>
            ),
            onClick: () => history.push(PATH_PREMIER_COLLECTION),
          },
        ];
      }

      if (isPremierCollectionEnabled) {
        return [
          {
            type: HeaderMenuButtonType.PremierCollection,
            selected: history.location.pathname.includes(
              PATH_PREMIER_COLLECTION
            ),
            content: (
              <Box className="funnel-button-content premier-collection">
                <Icon name={IconName.PremierCollectionVerticalIcon} />
              </Box>
            ),
            onClick: () => history.push(PATH_PREMIER_COLLECTION),
          },
        ];
      }

      if (isLifestyleOnlyEnabled && !isVRForNonPremiumCardHoldersEnabled) {
        return [
          {
            type: HeaderMenuButtonType.PremierCollection,
            selected: history.location.pathname.includes(
              PATH_PREMIER_COLLECTION
            ),
            content: (
              <Box className="funnel-button-content lifestyle-collection">
                <Icon name={IconName.LifestyleCollectionIcon} />
              </Box>
            ),
            onClick: () => history.push(PATH_PREMIER_COLLECTION),
          },
        ];
      }

      return [];
    };

  const packagesButton = isPackagesExperiment
    ? [
        {
          type: HeaderMenuButtonType.Packages,
          selected: history.location.pathname === PATH_PACKAGES,
          content: (
            <Box className="funnel-button-content">
              <div className="packages-icon-wrapper">
                <Icon name={IconName.HotelFunnelIcon} />
                <Typography className="icon-plus-separator">+</Typography>
                <Icon name={IconName.FlightFunnelIcon} />
              </div>
              <Typography>{getPackagesDisplayName()}</Typography>
              <FunnelNewTag
                enabled
                type={FunnelNewTagType.PACKAGES_FUNNEL_SEARCHES}
                className={isCorporate ? "corp-new-funnel-tag" : undefined}
              />
            </Box>
          ),
          onClick: () => {
            if (showPackagesHelpTip) {
              handleClosePackagesHelpTip();
            }
            history.push(PATH_PACKAGES);
          },
        },
      ]
    : [];

  const experiencesButton = isExperiencesExperiment
    ? [
        {
          type: HeaderMenuButtonType.Experiences,
          selected: history.location.pathname === PATH_EXPERIENCES,
          content: (
            <Box className="funnel-button-content">
              <div className="experiences-icon-wrapper">
                <Icon name={IconName.ExperiencesIcon} />
              </div>
              <Typography>Activities</Typography>
              <FunnelNewTag
                enabled
                type={FunnelNewTagType.EXPERIENCES_FUNNEL_SEARCHES}
              />
            </Box>
          ),
          onClick: () => history.push(PATH_EXPERIENCES),
        },
      ]
    : [];

  const buttons = [
    flightsButton,
    hotelsButton,
    ...packagesButton,
    carsButton,
    ...experiencesButton,
    ...(showPremiumFunnel ? getPremiumStaysButton() : []),
  ];

  const getButtons = (variant: RestrictFunnelsVariantType) => {
    switch (variant) {
      case CARS_ONLY:
        return [carsButton];
      case FLIGHTS_ONLY:
        return [flightsButton];
      case HOTELS_ONLY:
        return [hotelsButton];
      case PREMIUM_STAYS_ONLY:
        return getPremiumStaysButton();
      case CONTROL:
      default:
        return buttons;
    }
  };

  return (
    <>
      <HeaderHomePageMiddleSection
        className={clsx("b2b", {
          mobile: isMobile,
        })}
        buttons={getButtons(restrictFunnelVariant)}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      />
      {isCorporate && isPackagesExperiment && (
        <HelpTip
          open={showPackagesHelpTip}
          onClose={handleClosePackagesHelpTip}
          anchor={{
            anchorRef: packagesButtonRef,
            placement: "bottom",
          }}
          title={
            <Typography
              component="span"
              className="helptip-title"
              style={{ fontWeight: 600, lineHeight: "24px" }}
            >
              Save time by booking your hotel and flight together.
            </Typography>
          }
          desc=""
          actions={
            <Box display="flex" justifyContent="flex-end" width="100%">
              <ActionButton
                defaultStyle="h4r-primary"
                onClick={() => {
                  handleClosePackagesHelpTip();
                  history.push(PATH_PACKAGES);
                }}
                message={`Try ${getPackagesDisplayName().toLowerCase()}`}
              />
            </Box>
          }
          showCloseButton
          enableClickAway={false}
          className="packages-educational-helptip"
          isNewFeature
          outlineColor={defaultCorpColors["parakeet-yellow"]}
          borderRadius="0"
          outlineOffset="-2px"
          backgroundColor={defaultCorpColors["blue-12"]}
        />
      )}
    </>
  );
};
